/**
 * The rollout (D40, phase 7f; the vendor's, never a customer control): the release the last publish started rolling
 * out, its status, each ring's gate as the scheduled evaluator judges it now — servers updated of total, productions
 * completed of those required, rejections, critical alarms on its updated servers, soak left — the rejections that
 * halt it, and earlier rollouts. A `vendor.admin` gets the actions its status allows (pause, resume, advance now,
 * halt, roll back); each opens `RolloutActionDialog` in the view.
 */
import {
  type ReleaseRingWire,
  type RingProgressWire,
  ROLLOUT_ACTION_FROM,
  ROLLOUT_ACTIONS,
  type RolloutActionWire,
  type RolloutStatusWire,
  type RolloutWire,
  type VendorRolloutResponseWire,
} from '@acp/api-contract/rollouts.js';
import { RefreshCwIcon } from 'lucide-react';
import { shortHash } from '../../lib/vendor.js';
import { UNKNOWN_HINT, UNKNOWN_LABEL } from '../../lib/openEnum.js';
import { Badge } from '../ui/badge.js';
import { Button } from '../ui/button.js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card.js';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table.js';

export type RolloutPanelProps = {
  readonly rollout: VendorRolloutResponseWire | null;
  readonly loading: boolean;
  readonly error: string | null;
  readonly canAdmin: boolean;
  readonly onRetry: () => void;
  /** Ask for an action on the current rollout (the view confirms it, with a reason). */
  readonly onAction: (action: RolloutActionWire, listHash: string) => void;
};

/** The actions a status allows: the contract's state machine (`ROLLOUT_ACTION_FROM`, which the server enforces), shown as buttons. */
function actionsFor(status: RolloutStatusWire): RolloutActionWire[] {
  return ROLLOUT_ACTIONS.filter((action) => ROLLOUT_ACTION_FROM[action].includes(status));
}

const ACTION_LABELS: Readonly<Record<RolloutActionWire, string>> = { pause: 'Pause', resume: 'Resume', advance: 'Advance now', halt: 'Halt', rollback: 'Roll back' };

const STATUS_VARIANTS: Readonly<Record<RolloutStatusWire, 'next' | 'warning' | 'destructive' | 'success' | 'secondary' | 'outline'>> = {
  rolling: 'next',
  paused: 'warning',
  halted: 'destructive',
  complete: 'success',
  'rolled-back': 'secondary',
  superseded: 'secondary',
  unknown: 'outline',
};

function StatusBadge({ status }: { readonly status: RolloutStatusWire }) {
  return (
    <Badge variant={STATUS_VARIANTS[status]} data-testid="rollout-status" title={status === 'unknown' ? UNKNOWN_HINT : undefined}>
      {status === 'unknown' ? UNKNOWN_LABEL : status}
    </Badge>
  );
}

function ringName(ring: ReleaseRingWire): string {
  return ring === 'unknown' ? UNKNOWN_LABEL : ring;
}

function minutesText(minutes: number): string {
  if (minutes < 60) return `${String(minutes)} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${String(hours)} h` : `${String(hours)} h ${String(rest)} min`;
}

function GateBadge({ progress }: { readonly progress: RingProgressWire }) {
  if (progress.openedAt === null) return <span className="text-muted-foreground">closed</span>;
  return (
    <Badge variant={progress.passed ? 'success' : 'standby'} data-testid="ring-gate">
      {progress.passed ? 'passes' : 'holds'}
    </Badge>
  );
}

function RingsTable({ progress }: { readonly progress: readonly RingProgressWire[] }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Ring</TableHead>
          <TableHead>Opened</TableHead>
          <TableHead>Servers updated</TableHead>
          <TableHead>Productions completed</TableHead>
          <TableHead>Rejections</TableHead>
          <TableHead>Critical alarms</TableHead>
          <TableHead>Soak left</TableHead>
          <TableHead>Gate</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {progress.map((p) => (
          <TableRow key={p.ring} data-testid="ring-row" data-ring={p.ring}>
            <TableCell className="font-medium">{ringName(p.ring)}</TableCell>
            <TableCell className="text-sm">{p.openedAt === null ? '—' : `${p.openedAt.slice(0, 16).replace('T', ' ')} UTC`}</TableCell>
            <TableCell data-testid="ring-updated">
              {String(p.updated)} / {String(p.servers)}
            </TableCell>
            <TableCell data-testid="ring-completed">
              {String(p.productionsCompleted)} / {String(p.gate.productionsCompleted)}
            </TableCell>
            <TableCell className={p.rejections > 0 ? 'text-destructive' : undefined}>{String(p.rejections)}</TableCell>
            <TableCell className={p.criticalAlarms > 0 ? 'text-destructive' : undefined}>{String(p.criticalAlarms)}</TableCell>
            <TableCell data-testid="ring-soak">{p.soakRemainingMinutes === null ? minutesText(p.gate.soakMinutes) : minutesText(p.soakRemainingMinutes)}</TableCell>
            <TableCell>
              <GateBadge progress={p} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function Actions({ rollout, onAction }: { readonly rollout: RolloutWire; readonly onAction: RolloutPanelProps['onAction'] }) {
  const actions = actionsFor(rollout.status);
  if (actions.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {actions.map((action) => (
        <Button
          key={action}
          size="sm"
          variant={action === 'halt' || action === 'rollback' ? 'destructive' : 'outline'}
          disabled={action === 'rollback' && rollout.previousListHash === null}
          data-testid={`rollout-${action}`}
          onClick={() => onAction(action, rollout.listHash)}
        >
          {ACTION_LABELS[action]}
        </Button>
      ))}
    </div>
  );
}

function Current({ view, rollout, canAdmin, onAction }: { readonly view: VendorRolloutResponseWire; readonly rollout: RolloutWire; readonly canAdmin: boolean; readonly onAction: RolloutPanelProps['onAction'] }) {
  return (
    <Card data-testid="rollout-current">
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center gap-2">
          Rollout of <span className="font-mono">{shortHash(rollout.listHash)}</span>
          <StatusBadge status={rollout.status} />
          {rollout.urgent ? <Badge variant="warning">urgent</Badge> : null}
        </CardTitle>
        <CardDescription>
          Started {rollout.startedAt.slice(0, 16).replace('T', ' ')} UTC by {rollout.startedBy}. Closed rings are served{' '}
          <span className="font-mono">{rollout.previousListHash === null ? 'the new release (there is no previous one)' : shortHash(rollout.previousListHash)}</span>. Pins still win.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4">
        {rollout.haltReason === null ? null : (
          <p role="alert" className="rounded-md border border-destructive p-3 text-sm text-destructive" data-testid="rollout-reason">
            {rollout.haltReason}
          </p>
        )}
        {canAdmin ? <Actions rollout={rollout} onAction={onAction} /> : null}
        <RingsTable progress={view.progress} />
        {view.rejections.length === 0 ? null : (
          <div className="grid gap-1 text-sm" data-testid="rollout-rejections">
            <p className="font-medium">Rejections (they halt the rollout; clear one in the releases below)</p>
            {view.rejections.map((r) => (
              <p key={`${r.tenantId}/${r.siteId}/${r.nodeId}`}>
                {r.tenantId}/{r.siteId}/{r.label} ({ringName(r.ring)}): {r.reason} at {r.at.slice(0, 16).replace('T', ' ')} UTC
              </p>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function History({ history }: { readonly history: readonly RolloutWire[] }) {
  if (history.length === 0) return null;
  return (
    <Card>
      <CardHeader>
        <CardTitle>Earlier rollouts</CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Release</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Started</TableHead>
              <TableHead>By</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {history.map((h) => (
              <TableRow key={h.listHash} data-testid="rollout-history-row">
                <TableCell className="font-mono text-xs">{shortHash(h.listHash)}</TableCell>
                <TableCell>
                  <StatusBadge status={h.status} />
                </TableCell>
                <TableCell className="text-sm">{h.startedAt.slice(0, 10)}</TableCell>
                <TableCell className="text-sm">{h.startedBy}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

export function RolloutPanel({ rollout, loading, error, canAdmin, onRetry, onAction }: RolloutPanelProps) {
  return (
    <div className="grid gap-6" data-testid="rollout-panel">
      {error === null ? null : (
        <div role="alert" className="flex items-center justify-between gap-4 rounded-md border border-destructive p-4 text-sm">
          <span className="text-destructive">Could not load the rollout: {error}</span>
          <Button variant="outline" size="sm" onClick={onRetry}>
            <RefreshCwIcon />
            Retry
          </Button>
        </div>
      )}
      {rollout === null && loading ? <p className="py-8 text-center text-sm text-muted-foreground">Loading the rollout…</p> : null}
      {rollout === null ? null : rollout.current === null ? (
        <p className="py-4 text-sm text-muted-foreground" data-testid="rollout-none">
          No rollout yet: the next release publish starts one, with the internal ring first.
        </p>
      ) : (
        <Current view={rollout} rollout={rollout.current} canAdmin={canAdmin} onAction={onAction} />
      )}
      {rollout === null ? null : <History history={rollout.history} />}
    </div>
  );
}
